'use strict';
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ensureTalkingHead, ensureHeadAudio, toScriptBlobUrl } from '../loaders';
import { getAvatarPreset, getDefaultAvatarPreset, getAvatarGlbUrl, getBustView } from '../presets';
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * TalkingHead (3D GLB) renderer — ported from `AvatarDisplay.vue`.
 *
 * Loads the TalkingHead library from CDN, shows a GLB avatar in the container,
 * and bridges the realtime audio playback into HeadAudio for lip-sync (the
 * HeadAudio `onvalue` callback writes viseme morph targets onto the TalkingHead
 * instance). The avatar is a passive renderer; it plays no audio of its own.
 */
export default class TalkingHeadRenderer {
    /**
     * @param ctx renderer context (container, asset base, audio accessors)
     * @param avatarId TalkingHead preset id (GLB filename, e.g. `'julia.glb'`)
     */
    constructor(ctx, avatarId) {
        this.head = null;
        this.headaudio = null;
        this.attachAudioPromise = null;
        this.mounted = false;
        this.ctx = ctx;
        this.avatarId = avatarId;
        this.paths = {
            processor: `${ctx.assetBase}/headworklet.min.mjs`,
            model: `${ctx.assetBase}/model-en-mixed.bin`,
            headaudio: `${ctx.assetBase}/headaudio.min.mjs`
        };
    }
    /** Initialize TalkingHead and bind the container. */
    initHead(TalkingHead) {
        if (this.head || !this.ctx.container || !TalkingHead)
            return;
        // Camera/lighting tuned to match the original widget avatar framing.
        this.head = new TalkingHead(this.ctx.container, {
            ttsEndpoint: '', // disable TTS (lip-sync is driven externally)
            lipsyncModules: [],
            ttsLang: '',
            ttsVoice: '',
            useTTS: false,
            cameraView: 'upper',
            mixerGainSpeech: 3,
            cameraDistance: -1.26,
            cameraRotateEnable: false,
            lightAmbientIntensity: 0.6,
            lightDirectIntensity: 0.4,
            lightSpotIntensity: 0
        });
    }
    mount() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.mounted)
                return;
            try {
                // Load TalkingHead (+ three) from CDN.
                const TalkingHead = yield ensureTalkingHead();
                this.initHead(TalkingHead);
                if (!this.head) {
                    throw new Error('TalkingHead initialization failed');
                }
                // Resolve preset → GLB URL.
                const preset = getAvatarPreset(this.avatarId) || getDefaultAvatarPreset();
                if (!getAvatarPreset(this.avatarId) && this.avatarId) {
                    console.warn(`[avatar] Avatar preset not found for "${this.avatarId}", using default: ${preset.avatarName}`);
                }
                const glbUrl = getAvatarGlbUrl(this.avatarId || preset.avatarId, this.ctx.avatarBase);
                yield this.head.showAvatar({ url: glbUrl, body: preset.body, avatarMood: 'neutral' });
                // The chat layout frames the avatar as a small round bust, so the construction-time
                // 'upper' view (head + torso, for the tall stage viewport) would leave a distant head in a
                // 108px circle. Applied after the GLB loads because that is when the camera has something
                // to frame; the stage is still faded out at this point, so there is no visible jump.
                if (this.ctx.framing === 'bust') {
                    const bust = getBustView(this.avatarId || preset.avatarId);
                    this.head.setView(bust.cameraView, {
                        cameraY: bust.cameraY,
                        cameraDistance: bust.cameraDistance
                    });
                }
                // Wire lip-sync if the audio context already exists (otherwise the
                // widget calls attachAudio() again after the session resumes audio).
                yield this.attachAudio();
                this.mounted = true;
                if (this.ctx.onLoaded)
                    this.ctx.onLoaded();
            }
            catch (e) {
                console.error('[avatar] TalkingHead setup failed:', e);
                if (this.ctx.onError)
                    this.ctx.onError({ message: e && e.message ? e.message : 'Avatar failed to load' });
            }
        });
    }
    attachAudio() {
        return __awaiter(this, void 0, void 0, function* () {
            const audioContext = this.ctx.getAudioContext();
            if (!audioContext || !this.head)
                return; // prerequisites not ready — a later call retries
            if (this.headaudio)
                return; // already attached
            if (!audioContext.audioWorklet) {
                console.warn('[avatar] AudioWorklet not supported — lip-sync disabled');
                return;
            }
            // attachAudio() is intentionally invoked more than once (from mount(),
            // then again once the session resumes its audio context). Both calls can
            // pass the guards above before `this.headaudio` is assigned (it's only set
            // after two awaits). Cache the in-flight promise so concurrent calls share
            // a single attach — otherwise we build two HeadAudio instances and leak the
            // first's gain-node tap.
            if (!this.attachAudioPromise) {
                this.attachAudioPromise = this.doAttachAudio(audioContext);
            }
            return this.attachAudioPromise;
        });
    }
    doAttachAudio(audioContext) {
        return __awaiter(this, void 0, void 0, function* () {
            const gainNode = this.ctx.getPlaybackGainNode();
            try {
                // 1. Load the HeadAudio AudioWorklet processor (idempotent). Blob-wrap
                //    so a CDN serving .mjs with a wrong/empty MIME type still loads.
                try {
                    const workletUrl = yield toScriptBlobUrl(this.paths.processor);
                    yield audioContext.audioWorklet.addModule(workletUrl);
                }
                catch (e) {
                    console.warn('[avatar] headworklet addModule:', e && e.message);
                }
                // 2. Load the HeadAudio class (ESM, from CDN/asset host).
                const HeadAudio = yield ensureHeadAudio(this.paths.headaudio);
                if (!HeadAudio) {
                    console.error('[avatar] HeadAudio class not found in headaudio.min.mjs');
                    return;
                }
                this.headaudio = new HeadAudio(audioContext);
                // 3. Load the lip-sync model.
                try {
                    yield this.headaudio.loadModel(this.paths.model);
                }
                catch (e) {
                    console.error('[avatar] Failed to load lip-sync model:', e);
                }
                // 4. Connect playback gain node → HeadAudio (analysis tap).
                if (gainNode) {
                    gainNode.connect(this.headaudio);
                }
                // 5. Viseme values → TalkingHead morph targets.
                this.headaudio.onvalue = (key, value) => {
                    if (this.head && this.head.mtAvatar && this.head.mtAvatar[key]) {
                        Object.assign(this.head.mtAvatar[key], { newvalue: value, needsUpdate: true });
                    }
                };
                this.head.opt.update = this.headaudio.update.bind(this.headaudio);
                // Gesture callbacks: glance at camera + speak-with-hands on speech start.
                let lastEnded = 0;
                this.headaudio.onended = () => { lastEnded = (typeof performance !== 'undefined' ? performance.now() : 0); };
                this.headaudio.onstarted = () => {
                    const now = (typeof performance !== 'undefined' ? performance.now() : 0);
                    if (now - lastEnded > 150) {
                        this.head.lookAtCamera(500);
                        this.head.speakWithHands();
                    }
                };
            }
            catch (err) {
                // Allow a later call to retry the attach (headaudio was never wired).
                this.attachAudioPromise = null;
                console.error('[avatar] Failed to init HeadAudio:', err);
            }
        });
    }
    setMood(mood) {
        if (this.head) {
            try {
                this.head.setMood(mood);
            }
            catch (e) {
                console.warn('[avatar] setMood failed:', e);
            }
        }
    }
    destroy() {
        if (this.head) {
            try {
                this.head.stop();
            }
            catch (e) { /* ignore */ }
            this.head = null;
        }
        if (this.headaudio) {
            const gainNode = this.ctx.getPlaybackGainNode();
            if (gainNode) {
                try {
                    gainNode.disconnect(this.headaudio);
                }
                catch (e) { /* ignore */ }
            }
            this.headaudio = null;
        }
        this.attachAudioPromise = null;
        if (this.ctx.container) {
            this.ctx.container.innerHTML = '';
        }
        this.mounted = false;
    }
}
